import {
    furthestProgress,
    mergeStatusMaps,
    type ItemStatus,
    type MasteryMap,
    type MasteryPoints,
    type Recommendation,
    type Score,
    type ScoreMap,
    type StatusMap,
} from "@khan/content-types";

import type {FpmPromotions} from "#exercises-package/types/shared-types.ts";
import {getUpdatedMasteryPoints} from "#mastery-utils-package/mastery-utils.ts";

import {
    CLEAR_RECOMMENDATION,
    LOAD_USER_INFO,
    SET_CURRENT_TOPIC,
    UPDATE_BEST_SCORE,
    UPDATE_FPM_MASTERY,
    UPDATE_FPM_MASTERY_COMPLETE,
    UPDATE_LAST_SECOND_WATCHED,
    UPDATE_LATEST_SCORE,
    UPDATE_PROGRESS,
    UPDATE_QUIZ_SCORE,
    UPDATE_RECOMMENDATIONS,
    UPDATE_STATUS,
    UPDATE_TASK_STATUS,
} from "./user-info-constants.ts";

export type State = {
    currentTopic: string | null | undefined;
    bestScoreMap: ScoreMap;
    latestScoreMap: ScoreMap;
    masteryMap: MasteryMap;
    unitMastery: Readonly<MasteryPoints>;
    subjectMastery: Readonly<MasteryPoints>;
    masteryUpdateComplete: boolean;
    quizScores: any;
    statusMap: StatusMap;
    recommendations: Array<Recommendation>;
    // A map from task keys (i.e., unique identifiers for tasks) to booleans
    // indcating whether the task is currently in-progress. Used to power the
    // start-of-task card logic, as we prefer to render the start-of-task card
    // prior to fetching the task itself, which requires us to track some state
    // around whether tasks are in-progress (in which case we omit the
    // start-of-task card).
    taskInProgressMap: Record<string, boolean>;
    lastSecondWatchedMap: Record<string, number>;
};

type QuizScore = any; // TODO (jsatk) define this

export type Action =
    | {
          type: "UPDATE_PROGRESS";
          fpmPromotions: FpmPromotions;
      }
    | {
          type: "SET_CURRENT_TOPIC";
          topicId: string;
          masteryEnabled: boolean;
      }
    | {
          type: "UPDATE_STATUS";
          status: ItemStatus;
          progressKey: string;
      }
    | {
          type: "CLEAR_RECOMMENDATION";
          kind: "Exercise" | "Video" | "Article";
          contentId: string;
      }
    | {
          // This action has too many callsites and all of them
          // only provide some of these values.
          // TODO(kevinb): make sure the callsites are using flow
          // TODO(kevinb): split up this action into small more
          // targetted actions.
          type: "LOAD_USER_INFO";
          userInfo: {
              masteryMap?: MasteryMap;
              masteryPercentage?: number;
              masteryPointsEarned?: number;
              masteryPointsAvailable?: number;
              subjectMasteryPercentage?: number;
              subjectMasteryPointsEarned?: number;
              subjectMasteryPointsAvailable?: number;
              // TODO
              quizScores?: any;
              recommendations?: Array<Recommendation>;
              statusMap?: StatusMap;
              taskInProgressMap?: Record<string, boolean>;
              bestScoreMap?: ScoreMap;
              latestScoreMap?: ScoreMap;
          };
      }
    | {
          type: "UPDATE_FPM_MASTERY";
          masteryMap: MasteryMap;
          masteryPercentage: number | null | undefined;
          masteryPointsEarned: number | null | undefined;
          masteryPointsAvailable: number | null | undefined;
      }
    | {
          type: "UPDATE_FPM_MASTERY_COMPLETE";
      }
    | {
          type: "UPDATE_RECOMMENDATIONS";
          recommendations: Array<Recommendation>;
      }
    | {
          type: "UPDATE_BEST_SCORE";
          bestScore: Score;
          progressKey: string;
      }
    | {
          type: "UPDATE_LATEST_SCORE";
          latestScore: Score;
          progressKey: string;
      }
    | {
          type: "UPDATE_QUIZ_SCORE";
          quizKey: string;
          quizScore: QuizScore;
      }
    | {
          type: "UPDATE_TASK_STATUS";
          taskKey: string;
          inProgress: boolean; // Is this right?
      }
    | {
          type: "UPDATE_LAST_SECOND_WATCHED";
          readableId: string;
          lastSecondWatched: number;
      };

function getUnitMastery(
    percentage: number,
    pointsEarned: number,
    pointsAvailable: number,
): MasteryPoints {
    return {
        percentage,
        pointsEarned,
        pointsAvailable,
    };
}

export const defaultState: State = {
    currentTopic: null,
    bestScoreMap: {},
    latestScoreMap: {},
    masteryMap: {},
    unitMastery: {
        percentage: 0,
        pointsEarned: 0,
        pointsAvailable: 0,
    },
    subjectMastery: {
        percentage: 0,
        pointsEarned: 0,
        pointsAvailable: 0,
    },
    masteryUpdateComplete: false,
    quizScores: {},
    recommendations: [],
    statusMap: {},
    taskInProgressMap: {},
    lastSecondWatchedMap: {},
};

const userInfoReducer = (state: State | undefined, action: Action): State => {
    if (!state) {
        return defaultState;
    }

    switch (action.type) {
        case UPDATE_PROGRESS: {
            const fpmPromotions = action.fpmPromotions || {};

            // NOTE: If we navigated from a unit to the subject/course page
            // pointsAvailable will not be zero so we'll update unitMastery
            // even though we're not on a unit page.  This is okay b/c when
            // we navigate back to a unit page we'll fetch the data from the
            // server for that unit.
            const unitMastery = getUpdatedMasteryPoints(
                state.unitMastery,
                state.masteryMap,
                fpmPromotions,
            );

            const subjectMastery = getUpdatedMasteryPoints(
                state.subjectMastery,
                state.masteryMap,
                fpmPromotions,
            );

            const updatedMasteryMap = {...state.masteryMap} as const;

            // Since exercises have the potential to cover other exercises
            // (in different units), the number of skills that get promoted may
            // exceed the the skill count in the current unit. This will cause
            // problems with our unit mastery calculation if we don't exclude those
            // covered exercises in our updated mastery map. The logic below assumes
            // the masteryMap refers to the mastery of the current scope.
            // FUTURE (tony): In the future, we will change the semantics of the
            // mastery map to be more consistent across the platform.
            Object.keys(state.masteryMap).forEach((progressKey) => {
                // @ts-expect-error - TS2542 - Index signature in type '{ readonly [x: string]: MasteryLevel; }' only permits reading.
                updatedMasteryMap[progressKey] =
                    fpmPromotions[progressKey] ||
                    updatedMasteryMap[progressKey];
            });

            return {
                ...state,
                masteryMap: updatedMasteryMap,
                subjectMastery,
                unitMastery,
            };
        }

        case SET_CURRENT_TOPIC:
            return {
                ...state,
                currentTopic: action.topicId,
            };

        case UPDATE_FPM_MASTERY_COMPLETE:
            return {
                ...state,
                masteryUpdateComplete: true,
            };

        case UPDATE_FPM_MASTERY:
            return {
                ...state,
                // FUTURE(kevinb): merge mastery maps so that we aren't blowing
                // away values from other units when we client-side nav... we're
                // currently loading that data whenever we switch units, but in
                // the future we might we could avoid it and save a request.
                // NOTE(angelo): Current code assumes masteryMap is only for the
                // current unit. Fix that first, then explore above.
                masteryMap: action.masteryMap,
                unitMastery:
                    typeof action.masteryPercentage === "number"
                        ? getUnitMastery(
                              action.masteryPercentage,
                              action.masteryPointsEarned || 0,
                              action.masteryPointsAvailable || 0,
                          )
                        : state.unitMastery,
            };

        case UPDATE_STATUS: {
            // The value of action.status comes from side-effects.js which has no
            // knowledge of user progress.  It only dispatches whether the user has
            // complete their current attempt of the exercise.  That means that if
            // a user has completed the exercise once before and then they try it
            // again, we'll get an action to update the status to 'started'.  If
            // the user were to exit the exercise they'd see their progress go
            // regress from 'complete' to 'started' which we don't want.
            //
            // To avoid that we use whichever status represents the furthest
            // progress.
            const current = state.statusMap[action.progressKey];
            const newStatus = furthestProgress(current, action.status);

            if (newStatus === current) {
                // No change so we return the current state.
                return state;
            }

            return {
                ...state,
                statusMap: {
                    ...state.statusMap,
                    [action.progressKey]: newStatus,
                },
            };
        }

        case UPDATE_TASK_STATUS:
            return {
                ...state,
                taskInProgressMap: {
                    ...state.taskInProgressMap,
                    [action.taskKey]: action.inProgress,
                },
            };

        case UPDATE_BEST_SCORE: {
            // NOTE(jeresig): UPDATE_BEST_SCORE is a way for updating the best
            // score achieved for an exercise on a topic page.
            let {bestScore} = action;
            const bestPercent = bestScore.numCorrect / bestScore.numAttempted;
            const oldScore = state.bestScoreMap[action.progressKey];

            if (
                oldScore &&
                oldScore.numCorrect / oldScore.numAttempted >= bestPercent
            ) {
                bestScore = oldScore;
            }

            return {
                ...state,
                bestScoreMap: {
                    ...state.bestScoreMap,
                    [action.progressKey]: bestScore,
                },
            };
        }

        case UPDATE_LATEST_SCORE: {
            const {latestScore} = action;

            return {
                ...state,
                latestScoreMap: {
                    ...state.latestScoreMap,
                    [action.progressKey]: latestScore,
                },
            };
        }

        case UPDATE_QUIZ_SCORE:
            return {
                ...state,
                quizScores: {
                    ...state.quizScores,
                    [action.quizKey]: action.quizScore,
                },
            };

        case UPDATE_RECOMMENDATIONS:
            return {
                ...state,
                recommendations: action.recommendations,
            };

        case CLEAR_RECOMMENDATION: {
            const constAction = action; // For flow
            return {
                ...state,
                recommendations: state.recommendations.filter(
                    ({contentId, kind}) =>
                        kind !== constAction.kind ||
                        contentId !== constAction.contentId,
                ),
            };
        }

        case LOAD_USER_INFO:
            // Prefer the state that's already in the store just in case it's been
            // updated since we last retreived data from the server.
            // TODO(kevinb) timestamp the data so we can overwrite it with new data
            // from the server if necessary.

            return {
                ...state,
                masteryMap: action.userInfo.masteryMap || state.masteryMap,
                unitMastery: getUnitMastery(
                    action.userInfo.masteryPercentage != null
                        ? action.userInfo.masteryPercentage
                        : state.unitMastery.percentage,
                    action.userInfo.masteryPointsEarned != null
                        ? action.userInfo.masteryPointsEarned
                        : state.unitMastery.pointsEarned,
                    action.userInfo.masteryPointsAvailable != null
                        ? action.userInfo.masteryPointsAvailable
                        : state.unitMastery.pointsAvailable,
                ),
                subjectMastery:
                    action.userInfo.subjectMasteryPercentage != null &&
                    action.userInfo.subjectMasteryPointsEarned != null &&
                    action.userInfo.subjectMasteryPointsAvailable != null
                        ? getUnitMastery(
                              action.userInfo.subjectMasteryPercentage,
                              action.userInfo.subjectMasteryPointsEarned,
                              action.userInfo.subjectMasteryPointsAvailable,
                          )
                        : state.subjectMastery,
                quizScores: {
                    ...action.userInfo.quizScores,
                    ...state.quizScores,
                },
                recommendations:
                    action.userInfo.recommendations || state.recommendations,
                statusMap: mergeStatusMaps(
                    action.userInfo.statusMap || {},
                    state.statusMap,
                ),
                taskInProgressMap: {
                    ...action.userInfo.taskInProgressMap,
                    ...(state.taskInProgressMap as any),
                },
                bestScoreMap: {
                    ...action.userInfo.bestScoreMap,
                    ...(state.bestScoreMap as any),
                },
                latestScoreMap: {
                    ...action.userInfo.latestScoreMap,
                    ...(state.latestScoreMap as any),
                },
            };

        case UPDATE_LAST_SECOND_WATCHED:
            if (
                action.lastSecondWatched === undefined ||
                action.lastSecondWatched ===
                    state.lastSecondWatchedMap[action.readableId]
            ) {
                return state;
            }
            return {
                ...state,
                lastSecondWatchedMap: {
                    ...state.lastSecondWatchedMap,
                    [action.readableId]: action.lastSecondWatched,
                },
            };

        default:
            return state;
    }
};

export default userInfoReducer;
